import React from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import upperFirst from "lodash/upperFirst";

import Tooltip from "common/DS/Tooltip";
import SVGIcon from "common/SVGIcon";
import TabList from "common/TabList";
import { APPLICATION_STATE, HIRE_STATE } from "lib/constants";
import { staticURL } from "lib/img";
import { applicationShape } from "lib/propTypes";
import { filterHardFails, filterSoftFails } from "redux/candidate/selectors";

const TAB = {
    ACTIVE: 1,
    INACTIVE: 2,
};

const TABS = [
    { text: "Active", value: TAB.ACTIVE },
    { text: "Inactive", value: TAB.INACTIVE },
];

const SORT = {
    // Top
    INTERVIEW_TODAY_FUTURE: 1,
    INTERVIEW_UNSCHEDULED: 2,
    INTERVIEW_PAST_NO_FEEDBACK: 3,

    // Bottom
    PENDING: 4,
    INTERVIEW_FUTURE: 5,
    INTERVIEW_PAST: 6,
    EMPLOYED: 7,
    UNREVIEWED: 8,
    UNAPPROVED: 9,
    UNFINISHED: 10,
};

const TOP_SORT = [
    SORT.INTERVIEW_TODAY_FUTURE,
    SORT.INTERVIEW_PAST_NO_FEEDBACK,
    SORT.INTERVIEW_UNSCHEDULED,
];

function sort(a, b) {
    if (a.sort === b.sort) {
        return a.sortTime < b.sortTime ? -1 : 1;
    }

    return a.sort < b.sort ? -1 : 1;
}

export default class CandidateMyApplications extends React.PureComponent {
    static propTypes = {
        applications: PropTypes.arrayOf(applicationShape).isRequired,
    };

    state = {
        activeTab: TAB.ACTIVE,
    };

    handleTabChange = (tab) => {
        this.setState({ activeTab: tab.value });
    };

    renderTap() {
        return (
            <>
                <span className="mobile-only">Tap</span>
                <span className="hidden-mobile">Click</span>
            </>
        );
    }

    renderApplication = (app) => {
        let stateString = null;
        let stateExtra = null;
        let stateURL = null;

        switch (app.sort) {
            case SORT.UNFINISHED:
                stateString = "Incomplete";
                stateExtra = <em>{this.renderTap()} to finish</em>;
                stateURL = `/app/start/?sn=2&p=${app.position.id}`;
                break;

            case SORT.UNAPPROVED:
                stateString = "In Process";
                if (app.auto_apply) {
                    stateExtra = (
                        <Tooltip title="Automatically applied based on your preferences">
                            <img src={staticURL("autoMatch.svg")} alt="" />
                        </Tooltip>
                    );
                }
                break;

            case SORT.UNREVIEWED:
                stateString = "In Review";
                break;

            case SORT.INTERVIEW_UNSCHEDULED:
                stateURL = app.schedule_url;
                stateString = upperFirst(app.position.interview_type.middle);
                stateExtra = <em>{this.renderTap()} to schedule</em>;
                break;

            case SORT.INTERVIEW_FUTURE:
                stateURL = `${app.schedule_url}?v`;
                stateString = upperFirst(app.position.interview_type.middle);
                stateExtra = <strong>{app.mTimeScheduled.format("MMM D, h:mma z")}</strong>;
                break;

            case SORT.INTERVIEW_TODAY_FUTURE:
                stateURL = `${app.schedule_url}?v`;
                stateString = upperFirst(app.position.interview_type.middle);
                stateExtra = (
                    <span>
                        <span className="my-apps__app-em">
                            Today at {app.mTimeScheduled.format("h:mma z")}
                        </span>
                    </span>
                );
                break;

            case SORT.INTERVIEW_PAST_NO_FEEDBACK:
                stateString = "Interviewed";
                stateExtra = <em>How did it go?</em>;
                stateURL = app.feedback_url;
                break;

            case SORT.INTERVIEW_PAST:
                stateString = "Interviewed";
                stateExtra = (
                    <strong className="my-apps__app-help">
                        <span>Pending feedback</span>
                        <Tooltip
                            title={`We're waiting on ${app.position.company_name} to provide us with an update. Note: You may have more up-to-date information directly from the company.`}
                        >
                            <SVGIcon icon="help-circle" />
                        </Tooltip>
                    </strong>
                );
                stateURL = null;
                break;

            case SORT.PENDING:
                stateString = "Moving forward";
                stateExtra = (
                    <strong className="my-apps__app-help">
                        <span>Coordinate directly</span>
                        <Tooltip
                            title={`${app.position.company_name} wants to move forward to the next step after the interview. You should coordinate directly with them about next steps, but let us know if you need any support!`}
                        >
                            <SVGIcon icon="help-circle" />
                        </Tooltip>
                    </strong>
                );
                break;

            case SORT.EMPLOYED: {
                const time = moment(app.hire.date_employed).format("MMM D");
                stateString = "Employed";
                stateExtra = <strong>Start date: {time}</strong>;
                break;
            }

            default:
                break;
        }

        /* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label */
        const el = stateURL ? <a href={stateURL} /> : <div />;
        /* eslint-enable */

        return React.cloneElement(
            el,
            {
                key: app.id,
                className: "my-apps__app",
            },
            <>
                <div className="my-apps__app-position">
                    <strong className="my-apps__app-position-title">{app.position.title}</strong>
                    <span className="my-apps__app-position-meta">
                        {app.position.company_name}
                        <br />
                        {app.position.location_name}
                    </span>
                </div>
                <div className="my-apps__app-state">
                    <span>{stateString}</span>
                    {stateExtra}
                </div>
            </>,
        );
    };

    renderActive() {
        const ACTIVE_STATES = [
            APPLICATION_STATE.STARTED,
            APPLICATION_STATE.APPLIED,
            APPLICATION_STATE.INTERVIEW,
            APPLICATION_STATE.HIRE_PENDING,
        ];

        const apps = filterSoftFails(filterHardFails(this.props.applications))
            .filter(
                (a) =>
                    ACTIVE_STATES.indexOf(a.state) > -1 ||
                    (a.state === APPLICATION_STATE.EMPLOYED &&
                        a.hire.state === HIRE_STATE.EMPLOYED),
            )
            .map((a) => {
                const isScheduled = !!a.time_interview_schedule;
                const mTimeScheduled = isScheduled ? moment(a.time_interview_schedule) : null;
                const app = {
                    ...a,
                    isScheduled,
                    mTimeScheduled,
                };

                if (app.state === APPLICATION_STATE.INTERVIEW) {
                    if (app.isScheduled) {
                        app.isScheduledToday = mTimeScheduled.isSame(moment(), "day");
                        app.isBeforeInterview = moment().isSameOrBefore(
                            mTimeScheduled.clone().add(60, "minutes"),
                        );

                        if (app.isBeforeInterview) {
                            app.sort = app.isScheduledToday
                                ? SORT.INTERVIEW_TODAY_FUTURE
                                : SORT.INTERVIEW_FUTURE;
                        } else {
                            app.sort = app.feedback_state
                                ? SORT.INTERVIEW_PAST
                                : SORT.INTERVIEW_PAST_NO_FEEDBACK;
                        }

                        app.sortTime = +mTimeScheduled;
                    } else {
                        app.sort = SORT.INTERVIEW_UNSCHEDULED;
                        app.sortTime = +moment(app.time_interview);
                    }
                } else if (app.state === APPLICATION_STATE.HIRE_PENDING) {
                    app.sort = SORT.PENDING;
                    app.sortTime = +moment(app.time_pending);
                } else if (app.state === APPLICATION_STATE.EMPLOYED) {
                    app.sort = SORT.EMPLOYED;
                    app.sortTime = +moment(app.hire.date_employed);
                } else if (app.state === APPLICATION_STATE.APPLIED) {
                    if (app.time_approved) {
                        app.sort = SORT.UNREVIEWED;
                        app.sortTime = +moment(app.time_approved);
                    } else {
                        app.sort = SORT.UNAPPROVED;
                        app.sortTime = +moment(app.time_submitted);
                    }
                } else {
                    app.sort = SORT.UNFINISHED;
                    app.sortTime = +moment(app.time_created);
                }

                return app;
            });

        const top = apps.filter((a) => TOP_SORT.indexOf(a.sort) > -1);
        const bottom = apps.filter((a) => TOP_SORT.indexOf(a.sort) === -1);

        top.sort(sort);
        bottom.sort(sort);

        const topNodes = top.map(this.renderApplication);
        const bottomNodes = bottom.map(this.renderApplication);

        return (
            <div className="my-apps__list">
                <div className="my-apps__list-top">{topNodes}</div>
                <div className="my-apps__list-bottom">{bottomNodes}</div>
            </div>
        );
    }

    renderInactive() {}

    renderTab() {
        if (this.state.activeTab === TAB.ACTIVE) {
            return this.renderActive();
        }

        return this.renderInactive();
    }

    render() {
        return (
            <div className="my-apps">
                <header className="my-apps__header my-apps__content">
                    <a className="my-apps__back-link" href="/" aria-label="Back">
                        <SVGIcon icon="chevron-left" />
                    </a>
                    <h1>My Applications</h1>
                    <img src={staticURL("application-invite-female.svg")} alt="invited" />
                </header>
                <section className="my-apps__content">
                    <TabList handleTabChange={this.handleTabChange} tabs={TABS} />
                    {this.renderTab()}
                </section>
            </div>
        );
    }
}
